import { Ant, AntState, ScentType, Position } from '../Ant';
import { TunnelNetwork, Chamber, ChamberType } from '../TunnelNetwork';
import { Rng } from '../../Rng';
import { refTicks } from '../../SimClock';

/** How close an ant must be to count as having arrived, in colony units. */
const ARRIVAL_RADIUS = 3;

/**
 * Context required for navigation operations.
 */
export interface NavigationContext {
  tunnelNetwork: TunnelNetwork;
  gameTimeMs: number;
  /**
   * The colony's generator. Passed through the context rather than held on the
   * system so NavigationSystem stays stateless, as documented below.
   */
  rng: Rng;
}

/**
 * NavigationSystem handles all ant movement and scent-following behavior.
 * This is a stateless system - all state lives on the Ant objects.
 */
export class NavigationSystem {
  /**
   * Set the scent an ant should follow.
   * This replaces explicit path targeting with emergent scent-following.
   */
  setScent(ant: Ant, scentType: ScentType, ctx: NavigationContext): void {
    ant.scentPreference = scentType;

    // Initialize chamber if not set
    if (ant.currentChamberId === null && ant.currentTunnelId === null) {
      const chamber = ctx.tunnelNetwork.getChamberAt(ant.position);
      if (chamber) {
        ant.currentChamberId = chamber.id;
      }
    }
  }

  /**
   * Set ant's path to an entrance (toward surface).
   */
  setPathToEntrance(ant: Ant): void {
    // Special case: navigate toward surface by preferring shallow chambers
    // We use NEST_ODOR but with inverted logic (seek weak scent)
    ant.scentPreference = ScentType.NEST_ODOR;
    ant.seekingEntrance = true; // Flag for special handling
  }

  /**
   * Set ant's path to a storage chamber.
   */
  setPathToStorage(ant: Ant, ctx: NavigationContext): void {
    // Follow food smell to find storage
    this.setScent(ant, ScentType.FOOD_SMELL, ctx);
  }

  /**
   * Set ant's path to the queen.
   */
  setPathToQueen(ant: Ant, ctx: NavigationContext): void {
    // Follow queen scent
    this.setScent(ant, ScentType.QUEEN_SCENT, ctx);
  }

  /**
   * Set ant's path to the nursery.
   */
  setPathToNursery(ant: Ant, ctx: NavigationContext): void {
    // Follow brood scent to find nursery
    this.setScent(ant, ScentType.BROOD_SCENT, ctx);
  }

  /**
   * Follow the current scent preference. Returns true if at a chamber with high scent.
   * Ants navigate by choosing tunnels based on scent gradients at junctions.
   */
  followScent(ant: Ant, dt: number, ctx: NavigationContext): boolean {
    const TUNNEL_SPEED = ant.speed * 60 * (dt / 1000); // Movement per frame

    // If in a tunnel, continue through it
    if (ant.currentTunnelId !== null) {
      const tunnel = ctx.tunnelNetwork.getTunnel(ant.currentTunnelId);
      if (!tunnel || tunnel.collapsed) {
        // Tunnel collapsed! Go back to last chamber
        ant.currentTunnelId = null;
        return false;
      }

      // Move through tunnel
      const progressDelta = TUNNEL_SPEED / Math.max(1, tunnel.length);
      ant.tunnelProgress += progressDelta;

      // Update position along tunnel
      const startPos = ant.movingTowardEnd ? tunnel.start : tunnel.end;
      const endPos = ant.movingTowardEnd ? tunnel.end : tunnel.start;
      ant.position.x = startPos.x + (endPos.x - startPos.x) * ant.tunnelProgress;
      ant.position.y = startPos.y + (endPos.y - startPos.y) * ant.tunnelProgress;

      // Deposit scent if carrying food (creates food trail)
      if (ant.carryingFood) {
        ctx.tunnelNetwork.depositScent(ant.currentTunnelId, ScentType.FOOD_TRAIL, 0.02 * (dt / 1000));
      }

      // Check if reached other end
      if (ant.tunnelProgress >= 1) {
        // Enter the chamber at the other end
        const targetChamberId = ant.movingTowardEnd
          ? tunnel.connectedChambers[1]
          : tunnel.connectedChambers[0];

        // Add this tunnel to recent list (avoid immediate backtracking)
        ant.recentTunnels.push(ant.currentTunnelId);
        if (ant.recentTunnels.length > 3) {
          ant.recentTunnels.shift(); // Keep only last 3
        }

        ant.currentChamberId = targetChamberId;
        ant.currentTunnelId = null;
        ant.tunnelProgress = 0;

        // Update position to chamber center
        const chamber = ctx.tunnelNetwork.getChamber(targetChamberId);
        if (chamber) {
          ant.position.x = chamber.position.x;
          ant.position.y = chamber.position.y;
        }
      }

      return false; // Still moving
    }

    // At a chamber - check if this is "destination"
    if (ant.currentChamberId !== null) {
      const chamber = ctx.tunnelNetwork.getChamber(ant.currentChamberId);
      if (!chamber || chamber.destroyed) {
        ant.currentChamberId = null;
        return false;
      }

      // Special case: seeking entrance (surface)
      const seekingEntrance = ant.seekingEntrance;
      if (seekingEntrance) {
        // Arrived if this is an entrance
        if (chamber.type === ChamberType.ENTRANCE) {
          ant.seekingEntrance = false;
          return true;
        }

        // Choose tunnel leading to shallower chamber (lower depth = closer to surface)
        const tunnelIds = ctx.tunnelNetwork.getConnectedTunnelIds(chamber.id);
        const validTunnels = tunnelIds.filter(tid => !ant.recentTunnels.includes(tid));
        const tunnelsToUse = validTunnels.length > 0 ? validTunnels : tunnelIds;

        // Find tunnel leading to shallowest chamber
        let bestTunnel: number | null = null;
        let shallowestDepth = chamber.depth;

        for (const tid of tunnelsToUse) {
          const otherChamber = ctx.tunnelNetwork.getOtherChamber(tid, chamber.id);
          if (otherChamber && otherChamber.depth < shallowestDepth) {
            shallowestDepth = otherChamber.depth;
            bestTunnel = tid;
          }
        }

        // If no shallower chamber, pick randomly
        if (bestTunnel === null && tunnelsToUse.length > 0) {
          bestTunnel = ctx.rng.pick(tunnelsToUse)!;
        }

        if (bestTunnel !== null) {
          const tunnel = ctx.tunnelNetwork.getTunnel(bestTunnel);
          if (tunnel) {
            ant.currentTunnelId = bestTunnel;
            ant.tunnelProgress = 0;
            ant.movingTowardEnd = (tunnel.connectedChambers[0] === ant.currentChamberId);
            ant.currentChamberId = null;
            ant.recentTunnels.push(bestTunnel);
            if (ant.recentTunnels.length > 3) ant.recentTunnels.shift();
          }
        }
        return false;
      }

      // Normal scent-following: check if chamber has high scent level
      const scentLevel = chamber.scents[ant.scentPreference];
      if (scentLevel > 0.5) {
        return true; // Arrived at destination
      }

      // Not at destination - choose a tunnel to enter based on scent gradient
      const tunnelId = ctx.tunnelNetwork.chooseTunnelByScent(
        ant.currentChamberId,
        ant.scentPreference,
        ant.recentTunnels,
        0.1 // 10% exploration chance
      );

      if (tunnelId === null) {
        // No valid tunnels - clear recent tunnels and try again, or wander
        if (ant.recentTunnels.length > 0) {
          ant.recentTunnels = []; // Allow backtracking
          // Try again with cleared history
          const retryTunnelId = ctx.tunnelNetwork.chooseTunnelByScent(
            ant.currentChamberId,
            ant.scentPreference,
            [],
            0.3
          );
          if (retryTunnelId !== null) {
            const tunnel = ctx.tunnelNetwork.getTunnel(retryTunnelId);
            if (tunnel) {
              ant.currentTunnelId = retryTunnelId;
              ant.tunnelProgress = 0;
              ant.movingTowardEnd = (tunnel.connectedChambers[0] === ant.currentChamberId);
              ant.currentChamberId = null;
              return false;
            }
          }
        }
        // Still no tunnel - wander within chamber
        this.wanderAnt(ant, dt, ctx);
        return false;
      }

      // Enter the chosen tunnel
      const tunnel = ctx.tunnelNetwork.getTunnel(tunnelId);
      if (tunnel) {
        ant.currentTunnelId = tunnelId;
        ant.tunnelProgress = 0;

        // Determine direction based on which chamber we're in
        ant.movingTowardEnd = (tunnel.connectedChambers[0] === ant.currentChamberId);

        ant.currentChamberId = null; // Now in tunnel, not chamber
      }

      return false;
    }

    // Neither in chamber nor tunnel - find nearest chamber
    const chambers = ctx.tunnelNetwork.getAllChambers();
    let nearest: Chamber | null = null;
    let nearestDist = Infinity;
    for (const c of chambers) {
      if (c.destroyed) continue;
      const dist = Math.sqrt(
        Math.pow(ant.position.x - c.position.x, 2) +
        Math.pow(ant.position.y - c.position.y, 2)
      );
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = c;
      }
    }

    if (nearest) {
      // Move toward nearest chamber
      ant.targetPosition = { ...nearest.position };
      if (this.moveTowardTarget(ant, dt)) {
        ant.currentChamberId = nearest.id;
      }
    }

    return false;
  }

  /**
   * Move ant toward its target position. Returns true if reached.
   */
  moveTowardTarget(ant: Ant, dt: number): boolean {
    if (!ant.targetPosition) return true;

    const dx = ant.targetPosition.x - ant.position.x;
    const dy = ant.targetPosition.y - ant.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= ARRIVAL_RADIUS) {
      ant.state = AntState.IDLE;
      return true;
    }

    // `ant.speed` is authored as units per reference tick.
    const step = ant.speed * refTicks(dt);

    // A step longer than the distance left would overshoot, and the ant would
    // oscillate around its target forever. At 60 Hz a step is about one unit
    // against an arrival radius of three, so this never fires; at the canonical
    // 6 Hz it is ten units and it fires constantly. Landing on the target is
    // correct at every cadence and is a no-op at the rate the game shipped with.
    if (step >= dist) {
      ant.position.x = ant.targetPosition.x;
      ant.position.y = ant.targetPosition.y;
      ant.state = AntState.IDLE;
      ant.stats.distanceTraveled += dist;
      return true;
    }

    ant.position.x += (dx / dist) * step;
    ant.position.y += (dy / dist) * step;
    ant.state = AntState.WALKING;
    ant.stats.distanceTraveled += step;

    return false;
  }

  /**
   * Make ant wander randomly within the colony.
   * Uses scent-following with high exploration to create natural wandering.
   */
  wanderAnt(ant: Ant, dt: number, ctx: NavigationContext): void {
    // Ensure ant has a scent preference (default to nest odor for general wandering)
    if (!ant.scentPreference) {
      ant.scentPreference = ScentType.NEST_ODOR;
    }

    // If in a chamber, randomly choose a tunnel (high exploration)
    if (ant.currentChamberId !== null) {
      const tunnelId = ctx.tunnelNetwork.chooseTunnelByScent(
        ant.currentChamberId,
        ant.scentPreference,
        ant.recentTunnels,
        0.5 // 50% exploration chance for wandering
      );

      if (tunnelId !== null) {
        const tunnel = ctx.tunnelNetwork.getTunnel(tunnelId);
        if (tunnel) {
          ant.currentTunnelId = tunnelId;
          ant.tunnelProgress = 0;
          ant.movingTowardEnd = (tunnel.connectedChambers[0] === ant.currentChamberId);
          ant.currentChamberId = null;
          ant.recentTunnels.push(tunnelId);
          if (ant.recentTunnels.length > 3) ant.recentTunnels.shift();
        }
      } else {
        // No valid tunnel found - clear recent tunnels to allow backtracking
        if (ant.recentTunnels.length > 0) {
          ant.recentTunnels = [];
        }
        // Also try wandering within the chamber
        const chamber = ctx.tunnelNetwork.getChamber(ant.currentChamberId);
        if (chamber) {
          // Small random movement within chamber
          const angle = ctx.rng.next() * Math.PI * 2;
          const dist = ctx.rng.next() * chamber.radius * 0.3;
          ant.targetPosition = {
            x: chamber.position.x + Math.cos(angle) * dist,
            y: chamber.position.y + Math.sin(angle) * dist,
          };
          this.moveTowardTarget(ant, dt);
        }
      }
    } else {
      // In a tunnel - continue through it using followScent
      this.followScent(ant, dt, ctx);
    }
  }

  /**
   * Check if ant is in a chamber (not traversing a tunnel).
   */
  isInChamber(ant: Ant): boolean {
    return ant.currentChamberId !== null;
  }

  /**
   * Get the current chamber the ant is in, or null if in tunnel.
   */
  getCurrentChamber(ant: Ant, ctx: NavigationContext): Chamber | null {
    if (ant.currentChamberId === null) return null;
    return ctx.tunnelNetwork.getChamber(ant.currentChamberId) || null;
  }

  /**
   * Check if ant is at its target position.
   */
  isAtTarget(ant: Ant): boolean {
    if (!ant.targetPosition) return true;
    const dx = ant.targetPosition.x - ant.position.x;
    const dy = ant.targetPosition.y - ant.position.y;
    return Math.sqrt(dx * dx + dy * dy) < 5;
  }
}
